import React, {
    useMemo,
    Fragment,
    memo,
    useEffect,
    useReducer,
    useCallback,
    useRef,
    useState,
} from 'react';

import { useWeb5Context } from 'lib/Web5Context';
import { generateTmpId } from 'lib/helpers';
import { Text, createUseStyles } from '@Sage/hoi-poi-ui';

import ForceForm, { validate, getDefaults } from 'components/ForceForm';
import HyperDrawer from 'components/HyperDrawer';
import { getValue, getValuesUsage } from 'lib/services/valueLists';

import styles from './styles';

const useStyles = createUseStyles(styles, 'FormValueListValue');

const initialState = {
    id: null,
    data: {},
    usage: null,
    list: [],
    errors: {},
    isOpen: false,
    isLoading: true,
    isSaving: false,
    isForDuplicate: false,
};

function reducer(state, action) {
    switch (action.type) {
        case 'init':
            return {
                ...state,
                data: action.data || {},
                list: action.list,
                errors: {},
                isOpen: true,
                isLoading: false, // true
            };
        case 'close':
            return { ...state, isSaving: false, isOpen: false };
        case 'changeData':
            return { ...state, data: action.data };
        case 'setErrors':
            return { ...state, isSaving: false, errors: action.errors };
        case 'setSaving':
            return {
                ...state,
                isSaving: action.value,
            };
        case 'setUsage':
            return {
                ...state,
                usage: action.data,
            };
        case 'setLoading':
            return {
                ...state,
                isLoading: action.value,
            };
        default:
            throw new Error('No action provided');
    }
}

const FormValueListValue = ({
    onRef,
    schema,
    stackIndex,
    isNewValueList,
    onStack,
    onSave,
    timeZone,
    valueListName,
    overrides,
}) => {
    const { t, language, fieldsToHide = [] } = useWeb5Context({ overrides });
    const [formState, setFormState] = useState({});
    const [state, dispatch] = useReducer(reducer, initialState);
    const formFields = useRef();
    const classes = useStyles();

    const overrideIsHidden = useCallback(
        (id) => Array.isArray(fieldsToHide) && fieldsToHide.includes(id),
        [fieldsToHide],
    );

    const insertData = useCallback(
        (data) => {
            if (!data) return;
            const finalData = { ...data };
            if (finalData.maxCount) delete finalData.maxCount;
            const keys = Object.keys(finalData);
            return (
                <Fragment>
                    {keys.map((key, index) => (
                        <div className={classes.entity} key={key}>
                            {index > 0 && <div className={classes.divider}></div>}
                            <Text className={classes.entityTitle} bold>
                                {key}
                            </Text>
                            {finalData[key]?.map((field) => (
                                <Text className={classes.field} key={field}>
                                    {`${t(field.description) || field.description2}: [${
                                        field.numItems <= data.maxCount
                                            ? field.numItems
                                            : t('cfm_label_related_entities_more_than', [
                                                  data.maxCount,
                                              ])
                                    }]`}
                                </Text>
                            ))}
                        </div>
                    ))}
                </Fragment>
            );
        },
        [classes.divider, classes.entity, classes.entityTitle, classes.field, t],
    );

    // Child method
    useEffect(() => {
        onRef &&
            onRef({
                open(data, list, valueListId) {
                    if (!data) data = { isNew: true };
                    // Hardcoding order default 0
                    formFields.current = { ...schema };

                    if (schema?.properties?.order) {
                        formFields.current = {
                            ...formFields.current,
                            properties: {
                                ...schema.properties,
                                order: {
                                    ...schema.properties.order,
                                    default: list?.length ? list.length : 0,
                                },
                            },
                        };
                    }

                    const otherCompositions = formFields.current.layout.composition.slice();
                    const firstComposition = otherCompositions.shift();

                    formFields.current = fieldsToHide.length
                        ? {
                              ...formFields.current,
                              layout: {
                                  ...formFields.current.layout,
                                  composition: [
                                      {
                                          ...firstComposition,
                                          composition: firstComposition.composition.map((field) => {
                                              field.isHidden =
                                                  overrideIsHidden(field.schemaId || field.id) ||
                                                  field.isHidden;
                                              return field;
                                          }),
                                      },
                                      ...(otherCompositions || []),
                                  ],
                              },
                          }
                        : formFields.current;

                    const valueUsageInfo = {
                        schemaId: 'valueUsageInfo',
                        uiType: 'message',
                        showCollapse: true,
                        showIcon: true,
                        title: (
                            <Text className={classes.adviceTitle} bold>
                                {t('cfm_label_related_entities')}
                            </Text>
                        ),
                    };

                    if (data?.id && !data.isNew)
                        getValuesUsage(valueListId, data?.id)
                            .then(({ data }) => {
                                dispatch({
                                    type: 'setUsage',
                                    data,
                                });
                            })
                            .catch((err) => console.error(err));

                    // Injecting label for special field idParent
                    if (data?.idParent && schema?.context?.fields?.idParent?.table) {
                        getValue(
                            schema?.context?.fields?.idParent?.table,
                            data?.idParent,
                            language,
                        ).then((res) => {
                            data.idParent = {
                                label: res.data?.description,
                                value: res.data?.id,
                            };
                            dispatch({
                                type: 'init',
                                list,
                                data: {
                                    ...getDefaults(formFields.current),
                                    ...data,
                                },
                            });
                        });
                    }
                    const fieldExists = formFields.current.layout.composition[0].composition.filter(
                        (el) => el.schemaId === 'valueUsageInfo',
                    );

                    if (!fieldExists.length)
                        formFields.current.layout.composition[0].composition.push(valueUsageInfo);

                    const timeZoneExists = Object.keys(schema.properties).find(
                        (el) => el === 'timeZoneId',
                    );

                    let finalData = { ...data };

                    if (data?.order)
                        finalData = {
                            ...finalData,
                            order: Number(data?.order),
                        };

                    if (timeZoneExists)
                        finalData = {
                            ...finalData,
                            timeZoneId: data.timeZoneId || {
                                label: timeZone?.value,
                                value: timeZone?.id,
                            },
                        };

                    dispatch({
                        type: 'init',
                        list,
                        data: {
                            ...getDefaults(formFields.current),
                            ...finalData,
                        },
                    });
                },
            });
    }, [
        onRef,
        onStack,
        schema,
        t,
        language,
        classes.adviceTitle,
        timeZone,
        overrideIsHidden,
        fieldsToHide,
    ]);

    const modifyFieldProps = useCallback(
        (field) => {
            let newField = field;
            switch (field.schemaId || field.id) {
                case 'order':
                    newField = {
                        ...newField,
                        info: t('cfm_label_value_total_values', [
                            formFields?.current?.properties?.order?.default || 0,
                        ]),
                    };
                    break;
                case 'dependingValues':
                    newField.isHidden = !formFields.current?.context?.fields?.idParent?.table;
                    break;
                case 'dependencyMessage':
                    newField = {
                        ...newField,
                        placeholder: t('cfm_label_value_depending_values_desc', [
                            formFields.current?.context?.fields?.idParent?.table,
                        ]),
                    };
                    break;
                case 'valueUsageInfo':
                    const keys = state.usage ? Object.keys(state.usage) : null;
                    newField = {
                        ...newField,
                        placeholder: state.usage && insertData(state.usage),
                        isHidden:
                            !keys ||
                            !(keys?.length > 1) ||
                            overrideIsHidden(field.schemaId || field.id),
                    };
                    break;
                case 'salesProbability':
                    newField = {
                        ...newField,
                        isReadOnly:
                            state.data.status?.value === 2 || state.data.status?.value === 3,
                    };
                    break;
                case 'extId':
                    newField = {
                        ...newField,
                        hint: t('cfm_label_extid_help'),
                    };
                    break;
                default:
                    switch (field.type) {
                        case 'Number':
                            newField = {
                                ...newField,
                                placeholder: t('cfm_placeholder_decimal'),
                            };
                            break;
                        case 'Integer':
                            newField = {
                                ...newField,
                                placeholder: t('cfm_placeholder_numeric'),
                            };
                            break;
                        case 'Datetime':
                            newField = {
                                ...newField,
                            };
                            break;
                        default:
                            break;
                    }
                    break;
            }
            return newField;
        },
        [insertData, overrideIsHidden, state.data.status?.value, state?.usage, t],
    );

    // Shared save
    const innerSave = useCallback(
        (data) => {
            if (!data.id && data.isNew) data.id = generateTmpId();
            onSave({ ...data, isTouched: !!(formState && Object.keys(formState).length) });
        },
        [formState, onSave],
    );

    const onCleaningProps = useCallback((data, properties) => {
        const keys = Object.keys(properties);
        return Object.keys(data).reduce((newData, key) => {
            keys.forEach((item) => {
                if (key === item) newData[key] = data[key];
            });

            return newData;
        }, {});
    }, []);

    const onSaveForm = useCallback(() => {
        let data = { ...state.data };
        const isNew = data.isNew;

        validate(data, formFields.current)
            .then(({ isValid, errors, cleanData }) => {
                if (!isValid) dispatch({ type: 'setErrors', errors });
                else {
                    let finalData = onCleaningProps(cleanData, schema.properties);

                    if (typeof finalData?.order === 'string')
                        finalData = {
                            ...finalData,
                            order: Number(finalData.order),
                        };

                    onStack(1);
                    dispatch({ type: 'close' });
                    innerSave({ id: data.id, ...finalData, isNew });
                }
            })
            .catch(console.error);
    }, [innerSave, onStack, onCleaningProps, schema.properties, state.data]);

    const onSaveCreate = useCallback(() => {
        let data = { ...state.data };
        const isNew = data.isNew;

        validate(data, formFields.current)
            .then(({ isValid, errors, cleanData }) => {
                if (!isValid) dispatch({ type: 'setErrors', errors });
                else {
                    // We should increment default order
                    if (formFields.current.properties.order) {
                        formFields.current = {
                            ...formFields.current,
                            properties: {
                                ...schema.properties,
                                order: {
                                    ...schema.properties.order,
                                    default: formFields.current.properties.order.default + 1,
                                },
                            },
                        };
                    }

                    let newData = {
                        isNew: true,
                        ...getDefaults(formFields.current),
                    };

                    let finalData = onCleaningProps(cleanData, schema.properties);
                    if (typeof finalData?.order === 'string')
                        finalData = {
                            ...finalData,
                            order: Number(finalData.order),
                        };
                    innerSave({ id: data.id, ...finalData, isNew });
                    dispatch({ type: 'init', data: newData });
                }
            })
            .catch(console.error);
    }, [innerSave, onCleaningProps, schema.properties, state.data]);

    const onCancel = useCallback(() => {
        dispatch({ type: 'close' });
        onStack(1);
    }, [onStack]);

    const onChange = useCallback(
        (data, field) => {
            let finalData = { ...data };
            if (valueListName === 'tblEstadoExpediente' && field === 'status') {
                const probabilityValue = finalData.status?.value === 2 ? 100 : 0;
                finalData = { ...finalData, salesProbability: probabilityValue };
                return dispatch({ type: 'changeData', data: finalData });
            }
            return dispatch({ type: 'changeData', data });
        },
        [valueListName],
    );

    const onBlur = useCallback((data, field) => {
        let finalData = { ...data };
        if (field === 'order') {
            finalData = { ...finalData, order: data?.order || 0 };
            return dispatch({ type: 'changeData', data: finalData });
        }
    }, []);

    const locales = useMemo(
        () => ({
            title: !state.data.id ? t('cfm_title_create_value') : t('cfm_title_update_value'),
            cancel: t('cfm_action_cancel'),
            save: !state.data.id ? t('cfm_action_add') : t('cfm_action_update'),
            saveAndCreate: !state.data.id
                ? t('cfm_action_add_not_close')
                : t('cfm_action_update_not_close'),
        }),
        [state.data.id, t],
    );

    return (
        <HyperDrawer
            stackIndex={stackIndex}
            isOpen={state.isOpen}
            locales={locales}
            isLoading={state.isLoading}
            isSaving={state.isSaving}
            onCancel={onCancel}
            onSave={onSaveForm}
            onSaveCreate={state.data.isNew && !state.data.id ? onSaveCreate : undefined}
            className="valuelist__add-value__crud__modal"
            insideCrud
        >
            <ForceForm
                onBlur={onBlur}
                onChange={onChange}
                onState={setFormState}
                values={state.data}
                errors={state.errors}
                schema={formFields.current}
                modifyFieldProps={modifyFieldProps}
                mode={isNewValueList ? 'create' : 'update'}
            />
        </HyperDrawer>
    );
};

FormValueListValue.propTypes = {};

export default memo(FormValueListValue);
